import { defineComponent as _defineComponent } from 'vue'
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import { inject, reactive, toRefs } from "vue";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import PostComplete from "@/components/communityTalk/PostComplete.vue";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import MailReceiveController from "@/controllers/communityTalk/mailReceiveController";
import { ApiErrorViewModel, ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import CustomInput from "@/components/common/form/CustomInput.vue";
import LoginUtil from "@/modules/utils/loginUtil";

interface State {
	/**
	 * 通知先メールアドレス。
	 */
	mailAddress: string;
	/**
	 * 通知先メールアドレス送付時のAPIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * APIバリデーションエラーオブジェクト。
	 */
	apiValidationErrorViewModel: ApiValidationErrorViewModel | null;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
}
// reactiveな変数宣言
interface Props {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * トークルームID。
	 */
	talkRoomId: string;
}

export default /*#__PURE__*/_defineComponent({
  props: {
    communityTalkStoreKey: { type: String, required: true },
    talkRoomId: { type: String, required: true }
  },
  setup(__props: any, { expose }) {
  expose()

const props = __props as {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * トークルームID。
	 */
	talkRoomId: string;
}
/**
 * トークルーム投稿 > メール通知送付。
 */

const state = reactive<State>({
	mailAddress: "",
	status: ApiPostLoadStatus.beforeRequest,
	apiValidationErrorViewModel: null,
	isProcessing: false,
});
const { mailAddress, status, apiValidationErrorViewModel, isProcessing } = toRefs(state);



// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

const mailReceiveController = new MailReceiveController();

const wordSettings = communityTalkStore.getWordSettings();

/**
 * ログイン状態でないと投稿不可かどうかのフラグ。
 * (現状、通知メール設定はトークルーム投稿時のみ行えるため、ここではトークルームの権限設定を参照する)
 */
const thisPostPermissionType = communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting;

/**
 * 通知先メールアドレス送付。
 */
const post = async (): Promise<void> => {
	state.isProcessing = true;
	// 入力欄が空であれば何もしない
	if (state.mailAddress === "") {
		state.isProcessing = false;
		return;
	}

	state.status = ApiPostLoadStatus.loading;
	try {
		// SiteUserIdの送信可否を確認
		// 不可である場合、undefindを送信する。
		let siteUserId = communityTalkStore.getSiteUserId();
		if (!LoginUtil.canSendSiteUserId(siteUserId, communityTalkStore.getGeneralSettings().nonMemberIdList, thisPostPermissionType)) {
			siteUserId = undefined;
		}

		// APIリクエスト
		await mailReceiveController.postNoticeTalkRoom(props.talkRoomId, state.mailAddress, communityTalkStore.getTenantId(), communityTalkStore.getReviCoCookieId(), siteUserId);
		state.status = ApiPostLoadStatus.success;
	} catch (e) {
		// バリデーションエラーであればエラーをを表示させ、もう1度入力できるようにする
		if ((e as ApiErrorViewModel).isValidationError) {
			state.apiValidationErrorViewModel = (e as ApiErrorViewModel).validationErrorContents;
			state.status = ApiPostLoadStatus.beforeRequest;
		} else {
			// そうでなければエラー扱い(入力はもう一度出来ない)
			state.status = ApiPostLoadStatus.error;
		}
	}
	state.isProcessing = false;
};

const __returned__ = { state, mailAddress, status, apiValidationErrorViewModel, isProcessing, props, communityTalkStore, mailReceiveController, wordSettings, thisPostPermissionType, post, ApiPostLoadMaster, PostComplete, CustomInput }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})